import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ExternalLink, Plus, Pencil, GraduationCap, UserPlus, Check, X, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CAT_BADGE = {
  gbe: "bg-[#003a5c]/10 text-[#003a5c] border-[#003a5c]/30",
  third_party: "bg-amber-50 text-amber-700 border-amber-200",
};
const CAT_LABEL = { gbe: "GBE", third_party: "Client / 3rd party" };

export default function InductionCatalog({ canEdit, onCatalog, onChanged }) {
  const [items, setItems] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [modal, setModal] = useState(null);      // catalogue entry form
  const [verifyRow, setVerifyRow] = useState(null); // {id, issue_date, expiry_date}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/inductions/catalog${canEdit ? "?include_inactive=true" : ""}`);
      setItems(data.items);
      onCatalog?.(Object.fromEntries(data.items.filter((i) => i.link).map((i) => [i.name, i.link])));
      if (canEdit) {
        const { data: p } = await api.get("/inductions/pending");
        setPendingRows(p.items);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Catalogue load failed"); }
  };
  useEffect(() => { load(); }, [canEdit]); // eslint-disable-line

  const save = async () => {
    if (!modal.name?.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/inductions/catalog", { ...modal, validity_months: modal.validity_months ? Number(modal.validity_months) : null });
      toast.success("Catalogue entry saved");
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inductions/catalog/seed");
      toast.success(`Seeded ${data.created} induction(s) from the register — add links via edit`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setBusy(false); }
  };

  const deleteEntry = async (it) => {
    if (!confirm(`Delete "${it.name}" from the catalogue? Existing register records are kept.`)) return;
    try {
      await api.delete(`/inductions/catalog/${it.id}`);
      toast.success(`"${it.name}" removed from catalogue`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const selfAdd = async (it) => {
    try {
      const { data } = await api.post("/inductions/self-add", { catalog_id: it.id });
      toast.success(`'${data.induction}' added to your record — pending HR verification`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };

  const doVerify = async () => {
    if (!verifyRow.issue_date) { toast.error("Issue date required"); return; }
    try {
      const { data } = await api.put(`/inductions/verify/${verifyRow.id}`, {
        issue_date: verifyRow.issue_date, expiry_date: verifyRow.expiry_date || null });
      toast.success(`Verified${data.expiry_date ? ` — expires ${data.expiry_date}` : ""}`);
      setVerifyRow(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Verify failed"); }
  };

  const doReject = async (id) => {
    if (!confirm("Reject and remove this pending induction request?")) return;
    try { await api.delete(`/inductions/pending/${id}`); toast.success("Request removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!items) {
    return <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading induction catalogue…</div>;
  }

  return (
    <div className="space-y-4 mb-5" data-testid="induction-catalog">
      {/* Pending verifications (HR) */}
      {canEdit && pendingRows.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="induction-pending-panel">
          <div className="text-xs font-semibold text-amber-800 mb-2">{pendingRows.length} induction request(s) awaiting verification</div>
          <div className="space-y-1.5">
            {pendingRows.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-xs bg-white border border-amber-100 rounded px-2 py-1.5">
                <span className="font-medium text-slate-800 w-40 truncate">{p.employee_name}</span>
                <span className="text-slate-600 flex-1 truncate">{p.induction_name} <span className="text-slate-400">({p.client})</span></span>
                <span className="text-slate-400 text-[10px]">{(p.requested_at || "").slice(0, 10)}</span>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" data-testid={`induction-verify-${p.id}`}
                        onClick={() => setVerifyRow({ id: p.id, issue_date: new Date().toISOString().slice(0, 10), expiry_date: "" })}>
                  <Check className="w-3 h-3 mr-0.5" />Verify</Button>
                <button className="text-slate-300 hover:text-rose-600" data-testid={`induction-reject-${p.id}`}
                        onClick={() => doReject(p.id)}><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Catalogue */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="w-4 h-4 text-gbe" />
          <span className="text-sm font-semibold text-slate-800">Induction catalogue</span>
          <span className="text-[11px] text-slate-500">Select an induction, complete it via its link, add it to your record for HR verification.</span>
          {canEdit && (
            <div className="ml-auto flex gap-2">
              {items.length === 0 && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={seed} disabled={busy}
                        data-testid="induction-seed">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Seed from register</Button>
              )}
              <Button size="sm" className="h-7 text-[11px] bg-gbe hover:bg-gbe-dark text-white" data-testid="induction-add-entry"
                      onClick={() => setModal({ name: "", category: "gbe", provider: "", link: "", validity_months: "", notes: "", active: true })}>
                <Plus className="w-3 h-3 mr-1" /> Add induction</Button>
            </div>
          )}
        </div>
        {items.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-6" data-testid="induction-catalog-empty">
            No inductions in the catalogue yet.{canEdit ? " Use 'Seed from register' to pre-load your existing induction types." : ""}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {items.map((it) => (
              <div key={it.id} data-testid={`induction-card-${it.id}`}
                   className={`rounded-lg border p-3 ${it.active ? "border-slate-200" : "border-slate-100 opacity-50"}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{it.name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="outline" className={`text-[9px] ${CAT_BADGE[it.category]}`}>{CAT_LABEL[it.category]}</Badge>
                      {it.provider && <span className="text-[10px] text-slate-500">{it.provider}</span>}
                      {it.validity_months && <span className="text-[10px] text-slate-400">· valid {it.validity_months}mo</span>}
                    </div>
                    {it.notes && <div className="text-[10px] text-slate-400 mt-1 line-clamp-2">{it.notes}</div>}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1.5">
                      <button className="text-slate-300 hover:text-slate-600" data-testid={`induction-edit-${it.id}`}
                              onClick={() => setModal({ ...it, validity_months: it.validity_months || "" })}>
                        <Pencil className="w-3 h-3" /></button>
                      <button className="text-slate-300 hover:text-rose-600" data-testid={`induction-delete-${it.id}`}
                              title="Delete from catalogue" onClick={() => deleteEntry(it)}>
                        <Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 mt-2.5">
                  {it.link ? (
                    <a href={it.link} target="_blank" rel="noopener noreferrer" data-testid={`induction-go-${it.id}`}
                       className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md bg-gbe text-white text-[11px] font-medium hover:bg-gbe-dark transition-colors">
                      <ExternalLink className="w-3 h-3" /> Go to induction</a>
                  ) : (
                    <span className="flex-1 inline-flex items-center justify-center h-7 rounded-md bg-slate-100 text-slate-400 text-[10px]">No link yet</span>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" data-testid={`induction-self-add-${it.id}`}
                          onClick={() => selfAdd(it)}>
                    <UserPlus className="w-3 h-3 mr-1" /> Add to my record</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Entry modal */}
      {modal && (
        <Overlay onClose={() => setModal(null)} title={modal.id ? "Edit induction" : "Add induction"} testid="induction-entry-modal">
          <Field label="Name"><Input value={modal.name} data-testid="induction-f-name"
                 onChange={(e) => setModal({ ...modal, name: e.target.value })} className="h-8" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={modal.category} onValueChange={(v) => setModal({ ...modal, category: v })}>
                <SelectTrigger className="h-8 text-xs" data-testid="induction-f-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gbe">GBE internal</SelectItem>
                  <SelectItem value="third_party">Third-party / Client</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Provider / client"><Input value={modal.provider || ""} placeholder="e.g. Ausgrid" data-testid="induction-f-provider"
                   onChange={(e) => setModal({ ...modal, provider: e.target.value })} className="h-8" /></Field>
          </div>
          <Field label="Direct link to induction"><Input value={modal.link || ""} placeholder="https://…" data-testid="induction-f-link"
                 onChange={(e) => setModal({ ...modal, link: e.target.value })} className="h-8" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Validity (months, optional)"><Input type="number" value={modal.validity_months} data-testid="induction-f-validity"
                   onChange={(e) => setModal({ ...modal, validity_months: e.target.value })} className="h-8" /></Field>
            <Field label="Active">
              <label className="flex items-center gap-2 h-8 text-xs cursor-pointer">
                <input type="checkbox" checked={modal.active} data-testid="induction-f-active"
                       onChange={(e) => setModal({ ...modal, active: e.target.checked })} className="accent-[#003a5c]" />
                Visible in catalogue</label>
            </Field>
          </div>
          <Field label="Notes"><Input value={modal.notes || ""} data-testid="induction-f-notes"
                 onChange={(e) => setModal({ ...modal, notes: e.target.value })} className="h-8" /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={save} data-testid="induction-entry-save">Save</Button>
          </div>
        </Overlay>
      )}

      {/* Verify modal */}
      {verifyRow && (
        <Overlay onClose={() => setVerifyRow(null)} title="Verify induction" testid="induction-verify-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issue date"><Input type="date" value={verifyRow.issue_date} data-testid="induction-v-issue"
                   onChange={(e) => setVerifyRow({ ...verifyRow, issue_date: e.target.value })} className="h-8" /></Field>
            <Field label="Expiry (blank = auto from validity)"><Input type="date" value={verifyRow.expiry_date} data-testid="induction-v-expiry"
                   onChange={(e) => setVerifyRow({ ...verifyRow, expiry_date: e.target.value })} className="h-8" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button size="sm" variant="outline" onClick={() => setVerifyRow(null)}>Cancel</Button>
            <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white" onClick={doVerify} data-testid="induction-v-save">
              <Check className="w-3.5 h-3.5 mr-1" /> Mark completed</Button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block text-xs mb-3"><span className="text-slate-600">{label}</span><div className="mt-1">{children}</div></label>;
}

function Overlay({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mt-16 p-5" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
